/**
 * <SmartIcon> - serves WebP when the browser supports it, falls back to PNG.
 *
 * Pair every PNG in `/images/toolbox-icons/**` with a matching `.webp`
 * generated at build/asset-prep time (scripts/convert_icons_to_webp.py).
 *
 * Drops in as a 1:1 replacement for <img src={path} className=... loading="lazy" decoding="async">.
 *
 *   <SmartIcon src="/images/toolbox-icons/matcher/architect.png"
 *              alt=""
 *              className="w-12 h-12 object-contain" />
 *
 * The browser picks WebP automatically via the <picture> element. If the
 * .webp doesn't exist on disk the <img> simply falls through to the PNG.
 */
import React from 'react';

export const SmartIcon = ({
  src,
  alt = '',
  className = '',
  loading = 'lazy',
  decoding = 'async',
  width,
  height,
  ...rest
}) => {
  // If the picked <source> (e.g. a .webp that doesn't exist on disk) fails to
  // load, <picture> does NOT auto-fall back to the <img>. Track that error and
  // re-render with only the plain PNG so we never show a broken-image icon.
  const [sourceFailed, setSourceFailed] = React.useState(false);
  React.useEffect(() => { setSourceFailed(false); }, [src]);
  if (!src) return null;
  // Only swap if the source looks like a PNG we control. Avoid mangling
  // remote URLs or other formats.
  const webp = (!sourceFailed && src.endsWith('.png'))
    ? src.replace(/\.png$/, '.webp')
    : null;
  return (
    <picture>
      {webp && <source srcSet={webp} type="image/webp" />}
      <img
        src={src}
        alt={alt}
        className={className}
        loading={loading}
        decoding={decoding}
        width={width}
        height={height}
        onError={() => { if (!sourceFailed) setSourceFailed(true); }}
        {...rest}
      />
    </picture>
  );
};

export default SmartIcon;
